import { useCallback, useEffect } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { useMutation, useQuery } from '@apollo/client'
import { useLink } from 'routes/cmsRoutes'
import { DELETE_QUESTION_BANK, GET_ONE_QUESTION_BANK, UPDATE_QUESTION_BANK } from 'queries/questionBanks'
import { GET_LIST_QUESTION_BANK_CATEGORY } from 'queries/questionBankCategory'
import { CreateQuestionBankInput } from '__generatedTypes__/globalTypes'
import { updateQuestionBank, updateQuestionBankVariables } from '__generatedTypes__/updateQuestionBank'
import { findOneQuestionBank, findOneQuestionBankVariables } from '__generatedTypes__/findOneQuestionBank'
import { removeQuestionBank, removeQuestionBankVariables } from '__generatedTypes__/removeQuestionBank'
import {
  findAllQuestionBankCategory,
  findAllQuestionBankCategoryVariables,
} from '__generatedTypes__/findAllQuestionBankCategory'
import { useHistory } from 'hooks/useHistory'
import { PageLoader } from 'components/PageLoader'
import { toast } from 'utils/toast'
import { Form, QuestionBank } from '../Form'
import { getCBSDashboardLink, getQBDashboardLink, isCaseScenarioSubject, isSubjectWithoutQBCategory } from '../utils'

export const Edit = () => {
  const { questionBankId = '' } = useParams()
  const { navigate } = useLink()
  const [searchParams] = useSearchParams()
  const { loadingHistory, populateHistoryWithUser } = useHistory()
  const [update] = useMutation<updateQuestionBank, updateQuestionBankVariables>(UPDATE_QUESTION_BANK)
  const [remove] = useMutation<removeQuestionBank, removeQuestionBankVariables>(DELETE_QUESTION_BANK)
  const { data, loading } = useQuery<findOneQuestionBank, findOneQuestionBankVariables>(GET_ONE_QUESTION_BANK, {
    variables: {
      params: questionBankId,
    },
    fetchPolicy: 'network-only',
  })
  const tabIndex = +(searchParams.get('tabIndex') ?? 0)
  const questionBank = data?.findOneQuestionBank
  const questions = data?.findQuestionByRelation ?? []
  const category = questionBank?.questionBankCategory
  const isWithoutCategory = isSubjectWithoutQBCategory(category?.subject)
  const categoriesState = useQuery<findAllQuestionBankCategory, findAllQuestionBankCategoryVariables>(
    GET_LIST_QUESTION_BANK_CATEGORY,
    {
      variables: {
        params: {
          where: {
            subject: {
              id: String(category?.subject?.id),
            },
          },
        },
      },
      skip: !category?.subject?.id || !isWithoutCategory,
    }
  )
  const categories = categoriesState.data?.findAllQuestionBankCategory.items ?? []

  const handleSubmit = useCallback(
    async (questionBank: CreateQuestionBankInput) => {
      try {
        const { data } = await update({
          variables: {
            params: {
              id: questionBankId,
              ...questionBank,
              history: populateHistoryWithUser(questionBank.history),
            },
          },
        })

        if (data?.updateQuestionBank.id) {
          if (isCaseScenarioSubject(category?.subject)) {
            navigate(getCBSDashboardLink(tabIndex))
          } else {
            navigate(getQBDashboardLink(tabIndex), undefined, {
              searchParams: new URLSearchParams({
                subjectId: String(category?.subject?.id),
                categoryId: String(category?.id),
                tabIndex: String(tabIndex),
              }),
            })
          }
          toast.success('Question bank updated successfully')
        } else {
          toast.error('Failed to update question bank')
        }
      } catch (error) {
        toast.error('Failed to update question bank')
      }
    },
    [populateHistoryWithUser, questionBankId, update, navigate, category]
  )

  const handleDelete = useCallback(async () => {
    try {
      const { data } = await remove({
        variables: {
          params: questionBankId,
        },
      })

      if (data?.removeQuestionBank) {
        if (isCaseScenarioSubject(category?.subject)) {
          navigate(getCBSDashboardLink(tabIndex))
        } else {
          navigate(getQBDashboardLink(tabIndex), undefined, {
            searchParams: new URLSearchParams({
              subjectId: String(category?.subject?.id),
              categoryId: String(category?.id),
              tabIndex: String(tabIndex),
            }),
          })
        }
        toast.success('Question bank removed successfully')
      } else {
        toast.error('Failed to remove question bank')
      }
    } catch (error) {
      toast.error('Failed to remove question bank')
    }
  }, [questionBankId, remove, navigate, category])

  useEffect(() => {
    if (!loading && (!questionBank || !category)) {
      navigate(getQBDashboardLink(tabIndex))
      toast.error('Failed to load data, redirecting to Question Bank List')
    }
  }, [loading, questionBank, category])

  if (loading || !questionBank || !category || (isWithoutCategory && !categories.length) || loadingHistory) {
    return <PageLoader />
  }

  return (
    <Form
      questionBankOrCategoryId={
        {
          ...questionBank,
          questions,
        } as QuestionBank
      }
      tabIndex={tabIndex}
      subject={category.subject}
      subjectCategories={categories}
      showMembershipLevelSelect={isWithoutCategory}
      onSubmit={handleSubmit}
      onDelete={handleDelete}
    />
  )
}
